import { closeBracketsKeymap, completionKeymap } from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { markdown, markdownKeymap, markdownLanguage } from "@codemirror/lang-markdown";
import { bracketMatching, indentOnInput } from "@codemirror/language";
import { Compartment, EditorState, type StateCommand } from "@codemirror/state";
import { drawSelection, EditorView, highlightSpecialChars, keymap, placeholder } from "@codemirror/view";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { cn } from "@/lib/utils";

import { autoPair } from "./autopair";
import { editingBindings } from "./keymap";
import { indentGuides, livePreview } from "./live-preview";
import { mathPreview } from "./math-preview";
import { graphmindMarkdown } from "./markdown-extensions";
import { graphmindEditorTheme } from "./theme";
import { wikilinkComplete } from "./wikilink-complete";

const PLACEHOLDER =
  "Write markdown… **bold**, *italic*, ==highlight==, [[wikilinks]], - lists, | tables |, $math$, ```code```";

/** The first image among a paste/drop `FileList`, or null if it carried no
 *  image (a normal text paste, or a drop of some other file type). */
function firstImageFile(list: FileList | null | undefined): File | null {
  if (!list) return null;
  for (const f of Array.from(list)) {
    if (f.type.startsWith("image/")) return f;
  }
  return null;
}

/** Imperative surface for the chrome around the editor (the formatting toolbar
 *  in MarkdownEditor): run a CodeMirror `StateCommand` — e.g. `wrapRange("**")`
 *  — against the live view exactly as the equivalent keybinding would, then
 *  return focus to the editor so a toolbar click never strands the caret. */
export interface CodeMirrorEditorHandle {
  runCommand: (cmd: StateCommand) => void;
  focus: () => void;
}

/** CodeMirror 6 surface for editing a markdown note. The document stays a plain
 *  markdown string (`onChange` receives the literal text); the optional Live
 *  Preview decoration layer renders markup inline without touching the text.
 *  Callbacks are held in refs so the view is created once and never torn down
 *  on re-render. */
export const CodeMirrorEditor = forwardRef<CodeMirrorEditorHandle, {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  livePreview: boolean;
  ariaLabel?: string;
  className?: string;
  /** An image file was pasted or dropped onto the editor (the paste/drop is
   *  preventDefault'd so the browser never navigates to it or inserts it as a
   *  data: URL). The caller owns uploading it and inserting the resulting
   *  markdown — see `MarkdownEditor`'s `insertImageFromFile`. */
  onImageFile?: (file: File) => void;
}>(function CodeMirrorEditor(
  { value, onChange, onSave, livePreview: live, ariaLabel = "Markdown source", className, onImageFile },
  ref,
) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const lpCompartment = useRef(new Compartment()).current;
  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);
  const onImageFileRef = useRef(onImageFile);
  onChangeRef.current = onChange;
  onSaveRef.current = onSave;
  onImageFileRef.current = onImageFile;

  // Create the EditorView once. `value`/`live` are seeds only here; the effects
  // below sync later changes without remounting (which would lose the caret).
  useEffect(() => {
    if (!host.current) return;
    const state = EditorState.create({
      doc: value,
      extensions: [
        history(),
        drawSelection(),
        highlightSpecialChars(),
        indentOnInput(),
        bracketMatching(),
        EditorView.lineWrapping,
        // `addKeymap: false` is load-bearing, not tidiness: `markdown()` other-
        // wise installs its own `Prec.high(keymap.of(markdownKeymap))`, which
        // outranks EVERY default-precedence binding no matter how the keymap
        // below is ordered. Enter inside a list item or blockquote then always
        // ran continue-list, so an open `[[…]]` suggestion could never be
        // accepted there ("- see [[Thr" + Enter → "- see [[Thr" / "- ]]").
        // Turning it off makes the `...markdownKeymap` entry below the real one.
        markdown({ base: markdownLanguage, extensions: graphmindMarkdown, addKeymap: false }),
        graphmindEditorTheme,
        // Indentation guides for nested/indented content — thin rails per level,
        // independent of the live-preview decoration layer (shown in Source too).
        indentGuides,
        autoPair,
        // A rendered formula floats above the caret while math is being typed —
        // the source stays editable, which the inline widget cannot offer.
        mathPreview,
        // `[[note name]]` suggestions off the search API. Its own keybindings are
        // disabled (`defaultKeymap: false`) so the single keymap below stays the
        // one place keystroke priority is decided.
        wikilinkComplete(),
        keymap.of([
          ...editingBindings(() => onSaveRef.current()),
          ...closeBracketsKeymap,
          // ORDER IS LOAD-BEARING: within one keymap the first binding that
          // returns true wins, and `markdownKeymap`/`defaultKeymap` below both
          // bind Enter (continue-list / insert-newline). completionKeymap must
          // come FIRST so Enter accepts an open suggestion; `acceptCompletion`
          // returns false when no popup is open, so list continuation still
          // works everywhere else. Same story for Escape and the arrow keys.
          // (Ordering only decides anything because `markdown()` above was told
          // NOT to install its own high-precedence copy of markdownKeymap.)
          ...completionKeymap,
          ...markdownKeymap,
          ...defaultKeymap,
          ...historyKeymap,
          // Tab indents / Shift-Tab dedents the current line(s) instead of
          // blurring the editor (the browser's default focus-navigation
          // behavior for Tab, which CodeMirror doesn't override on its own).
          indentWithTab,
        ]),
        placeholder(PLACEHOLDER),
        EditorView.contentAttributes.of({ "aria-label": ariaLabel, spellcheck: "true" }),
        // Pasting or dropping an image file hands it to the caller (upload +
        // insert `![](path)`) instead of the browser's default (a giant
        // data: URL paste, or navigating the tab to the dropped file). A drop
        // also moves the caret to the drop point first, so the image lands
        // roughly where the user aimed once the upload resolves.
        EditorView.domEventHandlers({
          paste(event) {
            const file = firstImageFile(event.clipboardData?.files);
            if (!file) return false;
            event.preventDefault();
            onImageFileRef.current?.(file);
            return true;
          },
          drop(event, v) {
            const file = firstImageFile(event.dataTransfer?.files);
            if (!file) return false;
            event.preventDefault();
            const pos = v.posAtCoords({ x: event.clientX, y: event.clientY });
            if (pos != null) v.dispatch({ selection: { anchor: pos } });
            onImageFileRef.current?.(file);
            return true;
          },
        }),
        lpCompartment.of(live ? livePreview : []),
        EditorView.updateListener.of((u) => {
          if (u.docChanged) onChangeRef.current(u.state.doc.toString());
        }),
      ],
    });
    const v = new EditorView({ state, parent: host.current });
    view.current = v;
    return () => {
      v.destroy();
      view.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull external value changes (initial seed, save-invalidation refresh, rename)
  // into the doc without clobbering the caret while the user is typing.
  useEffect(() => {
    const v = view.current;
    if (!v) return;
    const cur = v.state.doc.toString();
    if (value !== cur) {
      v.dispatch({ changes: { from: 0, to: cur.length, insert: value } });
    }
  }, [value]);

  // Toggle the Live Preview decoration layer in place (Compartment reconfigure).
  useEffect(() => {
    const v = view.current;
    if (!v) return;
    v.dispatch({ effects: lpCompartment.reconfigure(live ? livePreview : []) });
  }, [live, lpCompartment]);

  // A `StateCommand` (CodeMirror's `(target: {state, dispatch}) => boolean`
  // shape) runs directly against an `EditorView` — it structurally satisfies
  // that target. Refocusing after dispatch matters because the caller is
  // always a toolbar button click, which otherwise leaves focus stranded off
  // the editor.
  useImperativeHandle(
    ref,
    () => ({
      runCommand: (cmd) => {
        const v = view.current;
        if (!v) return;
        cmd(v);
        v.focus();
      },
      focus: () => view.current?.focus(),
    }),
    [],
  );

  return <div ref={host} className={cn("h-full w-full overflow-hidden", className)} />;
});
